import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/i18nContext';
import Button from '../../components/ui/Button';
import '../../styles/AdminEntity.css';

const dateOnly = (v) => (v ? String(v).substring(0, 10) : '');

const PROPERTY_TYPES = ['villa', 'apartment', 'finca', 'townhouse', 'other'];

const Field = ({ form, onChange, label, name, type = 'text', ...rest }) => (
  <div className="ae-field">
    <label>{label}</label>
    <input name={name} type={type} value={form[name] ?? ''} onChange={onChange} {...rest} />
  </div>
);

const CheckField = ({ form, onChange, label, name }) => (
  <div className="ae-checkbox-row">
    <input type="checkbox" id={`chk-${name}`} name={name} checked={!!form[name]} onChange={onChange} />
    <label htmlFor={`chk-${name}`}>{label}</label>
  </div>
);

const EMPTY_FORM = {
  user_id: '', preferred_agent_id: '', name: '', type: 'villa',
  address_street: '', address_city: '', address_postal_code: '',
  address_province: '', address_country: 'Spain',
  gps_lat: '', gps_lng: '', cadastral_ref: '',
  area_m2: '', plot_m2: '', bedrooms: '', bathrooms: '',
  has_pool: false, has_garage: false, has_garden: false,
  key_location: '', alarm_code: '', gate_code: '',
  electricity_account: '', electricity_provider: '',
  water_account: '', water_provider: '',
  gas_account: '', gas_provider: '',
  internet_account: '', internet_provider: '',
  legal_owner_name: '',
  community_fee: '', community_fee_notes: '',
  ibi_amount: '', ibi_due_date: '',
  insurance_company: '', insurance_policy_number: '', insurance_expiry: '',
  notes: '', is_active: true,
};

const PropertiesPage = () => {
  const { api } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // A szerkesztett ingatlanhoz tartozó szerződések (csak megjelenítés)
  const [relatedContracts, setRelatedContracts] = useState([]);

  const [properties, setProperties] = useState([]);
  const [users, setUsers]           = useState([]);
  const [agents, setAgents]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [msg, setMsg]               = useState({ text: '', type: '' });

  const [editing, setEditing]   = useState(null); // null = list, 'new' | id = form
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);

  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting]       = useState(false);
  const importRef                       = useRef();

  const handleExport = async (template) => {
    try {
      const r = await api.get(`/properties/export${template ? '?template=1' : ''}`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = template ? 'properties_template.xlsx' : 'properties_export.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { setMsg({ text: 'Export failed', type: 'error' }); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    setImportResult(null);
    try {
      const buffer = await file.arrayBuffer();
      const r = await api.post('/properties/import', buffer, { headers: { 'Content-Type': 'application/octet-stream' } });
      setImportResult(r.data);
      if (r.data.created > 0 || r.data.updated > 0) load();
    } catch (err) {
      setMsg({ text: err.response?.data?.error || 'Import failed', type: 'error' });
    } finally { setImporting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, uRes, aRes] = await Promise.all([
        api.get('/properties'),
        api.get('/admin/users'),
        api.get('/cases/agents'),
      ]);
      setProperties(pRes.data);
      setUsers(uRes.data.users || []);
      setAgents(aRes.data || []);
    } catch { setMsg({ text: 'Failed to load data', type: 'error' }); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Auto-open property from ?edit=:id URL param (e.g. linked from user profile)
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && !loading) {
      openEdit(Number(editId));
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const flash = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 4000);
  };

  const openNew = () => { setForm(EMPTY_FORM); setRelatedContracts([]); setEditing('new'); };

  const openEdit = async (id) => {
    try {
      const r = await api.get(`/properties/${id}`);
      const p = r.data;
      setForm({
        user_id: p.user_id ?? '',
        preferred_agent_id: p.preferred_agent_id ?? '',
        name: p.name ?? '',
        type: p.type ?? 'villa',
        address_street: p.address_street ?? '',
        address_city: p.address_city ?? '',
        address_postal_code: p.address_postal_code ?? '',
        address_province: p.address_province ?? '',
        address_country: p.address_country ?? 'Spain',
        gps_lat: p.gps_lat ?? '',
        gps_lng: p.gps_lng ?? '',
        cadastral_ref: p.cadastral_ref ?? '',
        area_m2: p.area_m2 ?? '',
        plot_m2: p.plot_m2 ?? '',
        bedrooms: p.bedrooms ?? '',
        bathrooms: p.bathrooms ?? '',
        has_pool: !!p.has_pool,
        has_garage: !!p.has_garage,
        has_garden: !!p.has_garden,
        key_location: p.key_location ?? '',
        alarm_code: p.alarm_code ?? '',
        gate_code: p.gate_code ?? '',
        electricity_account: p.electricity_account ?? '',
        electricity_provider: p.electricity_provider ?? '',
        water_account: p.water_account ?? '',
        water_provider: p.water_provider ?? '',
        gas_account: p.gas_account ?? '',
        gas_provider: p.gas_provider ?? '',
        internet_account: p.internet_account ?? '',
        internet_provider: p.internet_provider ?? '',
        legal_owner_name: p.legal_owner_name ?? '',
        community_fee: p.community_fee ?? '',
        community_fee_notes: p.community_fee_notes ?? '',
        ibi_amount: p.ibi_amount ?? '',
        ibi_due_date: p.ibi_due_date ?? '',
        insurance_company: p.insurance_company ?? '',
        insurance_policy_number: p.insurance_policy_number ?? '',
        insurance_expiry: p.insurance_expiry ? p.insurance_expiry.substring(0, 10) : '',
        notes: p.notes ?? '',
        is_active: p.is_active !== 0,
      });
      // A szerződéslista hiánya ne akadályozza meg az ingatlan szerkesztését
      try {
        const cr = await api.get(`/contracts?property_id=${id}`);
        setRelatedContracts(cr.data);
      } catch { setRelatedContracts([]); }
      setEditing(id);
    } catch { flash('Failed to load property', 'error'); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/properties', form);
        flash('Property created');
      } else {
        await api.put(`/properties/${editing}`, form);
        flash('Property updated');
      }
      setEditing(null);
      load();
    } catch (err) {
      flash(err.response?.data?.error || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/properties/${id}`);
      setProperties(p => p.filter(x => x.id !== id));
      flash('Deleted');
    } catch (err) { flash(err.response?.data?.error || 'Delete failed', 'error'); }
  };

  const filtered = properties.filter(p =>
    [p.name, p.owner_email, p.owner_username, p.address_city]
      .join(' ').toLowerCase().includes(search.toLowerCase())
  );

  if (editing !== null) {
    return (
      <div className="ae-page">
        {msg.text && <div className={`ae-msg ${msg.type}`}>{msg.text}</div>}
        <div className="ae-card">
          <div className="ae-form-header">
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>← Back</Button>
            <h2 className="ae-form-title">{editing === 'new' ? 'New Property' : 'Edit Property'}</h2>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.65rem' }}>
              <Button variant="ghost" type="button" onClick={() => setEditing(null)}>Cancel</Button>
              <Button variant="solid" type="submit" form="property-form" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
          <form id="property-form" onSubmit={handleSave}>
            <div className="ae-form-body">

              <div className="ae-form-section">
                <p className="ae-form-section-title">Basic</p>
                <div className="ae-grid">
                  <div className="ae-field">
                    <label>Owner *</label>
                    <select name="user_id" value={form.user_id} onChange={handleChange} required>
                      <option value="">— select user —</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                      ))}
                    </select>
                  </div>
                  <div className="ae-field">
                    <label>Preferred agent</label>
                    <select name="preferred_agent_id" value={form.preferred_agent_id} onChange={handleChange}>
                      <option value="">— none —</option>
                      {agents.map(a => (
                        <option key={a.id} value={a.id}>{a.username} ({a.email})</option>
                      ))}
                    </select>
                  </div>
                  <Field form={form} onChange={handleChange} label="Name *" name="name" required />
                  <div className="ae-field">
                    <label>Type</label>
                    <select name="type" value={form.type} onChange={handleChange}>
                      {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <CheckField form={form} onChange={handleChange} label="Active" name="is_active" />
                </div>
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">Address</p>
                <div className="ae-grid">
                  <Field form={form} onChange={handleChange} label="Street" name="address_street" />
                  <Field form={form} onChange={handleChange} label="City" name="address_city" />
                  <Field form={form} onChange={handleChange} label="Postal code" name="address_postal_code" />
                  <Field form={form} onChange={handleChange} label="Province" name="address_province" />
                  <Field form={form} onChange={handleChange} label="Country" name="address_country" />
                  <Field form={form} onChange={handleChange} label="Cadastral ref" name="cadastral_ref" />
                  <Field form={form} onChange={handleChange} label="GPS lat" name="gps_lat" type="number" step="any" />
                  <Field form={form} onChange={handleChange} label="GPS lng" name="gps_lng" type="number" step="any" />
                  {form.gps_lat !== '' && form.gps_lng !== '' && (
                    <div className="ae-field" style={{ justifyContent: 'flex-end' }}>
                      <label>&nbsp;</label>
                      <a
                        href={`https://www.google.com/maps?q=${form.gps_lat},${form.gps_lng}`}
                        target="_blank" rel="noopener noreferrer"
                        className="ae-gps-link"
                      >
                        📍 Open in Google Maps
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">Physical</p>
                <div className="ae-grid">
                  <Field form={form} onChange={handleChange} label="Area m²" name="area_m2" type="number" step="0.01" />
                  <Field form={form} onChange={handleChange} label="Plot m²" name="plot_m2" type="number" step="0.01" />
                  <Field form={form} onChange={handleChange} label="Bedrooms" name="bedrooms" type="number" />
                  <Field form={form} onChange={handleChange} label="Bathrooms" name="bathrooms" type="number" />
                  <CheckField form={form} onChange={handleChange} label="Pool" name="has_pool" />
                  <CheckField form={form} onChange={handleChange} label="Garage" name="has_garage" />
                  <CheckField form={form} onChange={handleChange} label="Garden" name="has_garden" />
                </div>
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">Access & Security</p>
                <div className="ae-grid">
                  <Field form={form} onChange={handleChange} label="Key location" name="key_location" />
                  <Field form={form} onChange={handleChange} label="Alarm code" name="alarm_code" />
                  <Field form={form} onChange={handleChange} label="Gate code" name="gate_code" />
                </div>
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">Utilities</p>
                <div className="ae-grid">
                  <Field form={form} onChange={handleChange} label="Electricity account" name="electricity_account" />
                  <Field form={form} onChange={handleChange} label="Electricity provider" name="electricity_provider" />
                  <Field form={form} onChange={handleChange} label="Water account" name="water_account" />
                  <Field form={form} onChange={handleChange} label="Water provider" name="water_provider" />
                  <Field form={form} onChange={handleChange} label="Gas account" name="gas_account" />
                  <Field form={form} onChange={handleChange} label="Gas provider" name="gas_provider" />
                  <Field form={form} onChange={handleChange} label="Internet account" name="internet_account" />
                  <Field form={form} onChange={handleChange} label="Internet provider" name="internet_provider" />
                </div>
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">Legal & Admin</p>
                <div className="ae-grid">
                  <Field form={form} onChange={handleChange} label="Legal owner" name="legal_owner_name" />
                  <Field form={form} onChange={handleChange} label="Community fee (€/yr)" name="community_fee" type="number" step="0.01" />
                  <Field form={form} onChange={handleChange} label="Community fee notes" name="community_fee_notes" />
                  <Field form={form} onChange={handleChange} label="IBI amount (€)" name="ibi_amount" type="number" step="0.01" />
                  <Field form={form} onChange={handleChange} label="IBI due date" name="ibi_due_date" />
                  <Field form={form} onChange={handleChange} label="Insurance company" name="insurance_company" />
                  <Field form={form} onChange={handleChange} label="Policy number" name="insurance_policy_number" />
                  <Field form={form} onChange={handleChange} label="Insurance expiry" name="insurance_expiry" type="date" />
                </div>
              </div>

              {editing !== 'new' && (
                <div className="ae-form-section">
                  <p className="ae-form-section-title">{t('contracts.related')}</p>
                  {relatedContracts.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                      {t('contracts.relatedNone')}
                    </p>
                  ) : (
                    <div className="ae-table-wrap">
                      <table className="ae-table">
                        <thead>
                          <tr>
                            <th>{t('contracts.number')}</th>
                            <th>{t('contracts.status')}</th>
                            <th>{t('contracts.term')}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {relatedContracts.map(c => (
                            <tr key={c.id}>
                              <td className="ae-name">{c.contract_number}</td>
                              <td>
                                <span className={`ae-badge ${c.status === 'active' ? 'active' : 'inactive'}`}>
                                  {t(`contracts.statuses.${c.status}`)}
                                </span>
                              </td>
                              <td>{dateOnly(c.start_date)} → {dateOnly(c.end_date) || t('contracts.openEnded')}</td>
                              <td>
                                <Button variant="ghost" size="sm" type="button"
                                  onClick={() => navigate(`/admin/contracts?edit=${c.id}`)}>
                                  {t('admin.edit')}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div className="ae-form-section">
                <p className="ae-form-section-title">Notes</p>
                <div className="ae-field">
                  <label>Notes</label>
                  <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} />
                </div>
              </div>

            </div>
            <div className="ae-form-footer">
              <Button variant="ghost" type="button" onClick={() => setEditing(null)}>Cancel</Button>
              <Button variant="solid" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="ae-page">
      {msg.text && <div className={`ae-msg ${msg.type}`}>{msg.text}</div>}

      {importResult && (
        <div className={`ae-msg ${importResult.errors?.length ? 'error' : importResult.warnings?.length ? 'warning' : 'success'}`} style={{ position: 'relative' }}>
          <button onClick={() => setImportResult(null)} style={{ position: 'absolute', right: '0.75rem', top: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem' }}>✕</button>
          <strong>Import complete:</strong> {importResult.created} created, {importResult.updated || 0} updated, {importResult.skipped} skipped.
          {importResult.warnings?.length > 0 && (
            <><div style={{ marginTop: '0.4rem', fontWeight: 600, fontSize: '0.8rem' }}>⚠ Warnings:</div>
            <ul style={{ margin: '0.2rem 0 0', paddingLeft: '1.2rem', fontSize: '0.8rem' }}>{importResult.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul></>
          )}
          {importResult.errors?.length > 0 && (
            <><div style={{ marginTop: '0.4rem', fontWeight: 600, fontSize: '0.8rem' }}>✕ Errors:</div>
            <ul style={{ margin: '0.2rem 0 0', paddingLeft: '1.2rem', fontSize: '0.8rem' }}>{importResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul></>
          )}
        </div>
      )}

      <div className="ae-card ae-header-card">
        <div className="ae-topbar">
          <h1 className="ae-title">Properties</h1>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button variant="ghost" size="sm" onClick={() => handleExport(false)}>↓ Export</Button>
            <Button variant="ghost" size="sm" onClick={() => handleExport(true)}>↓ Template</Button>
            <Button variant="ghost" size="sm" onClick={() => importRef.current?.click()} disabled={importing}>
              {importing ? 'Importing…' : '↑ Import XLSX'}
            </Button>
            <input ref={importRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: 'none' }} onChange={handleImport} />
            <Button variant="solid" onClick={openNew}>+ Add property</Button>
          </div>
        </div>
        <div className="ae-filters">
          <input
            className="ae-search"
            placeholder="Search by name, owner, city…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="ae-card">
        {loading ? (
          <div className="ae-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="ae-empty">No properties found</div>
        ) : (
          <div className="ae-table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Owner</th>
                  <th>Agent</th>
                  <th>City</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id}>
                    <td className="ae-name">{p.name}</td>
                    <td>{p.owner_username || p.owner_email || p.user_id}</td>
                    <td>{p.preferred_agent_username || <span style={{color:'var(--text-muted)'}}>—</span>}</td>
                    <td>{p.address_city || '—'}</td>
                    <td>{p.type}</td>
                    <td>
                      <span className={`ae-badge ${p.is_active ? 'active' : 'inactive'}`}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="ae-actions">
                        <Button variant="ghost" size="sm"
                          title={t('contracts.title')}
                          onClick={() => navigate(`/admin/contracts?property_id=${p.id}`)}>
                          📄
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p.id)}>Edit</Button>
                        <Button variant="danger" size="sm" onClick={() => handleDelete(p.id, p.name)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertiesPage;
